import React, { useCallback, useEffect, useState } from 'react';
import EyeOffIcon from '@heroicons/react/solid/EyeOffIcon.js';
import clsx from 'clsx';
import { Link as RouterLink } from 'react-router-dom';
import { hasScopesPermission } from '@tribeplatform/gql-client/lib';
import { TagPillWithLink, useQuery, } from '@tribeplatform/react-components/common';
import { T } from '@tribeplatform/react-components/i18n';
import { useAuthMember, useSpace, useSpaceUtil, useUpdateSpaceHighlightedTags, } from '@tribeplatform/react-sdk/hooks';
import { useSlate } from '@tribeplatform/react-slate-kit/hooks';
import { Button } from '@tribeplatform/react-ui-kit/Button';
import { Card } from '@tribeplatform/react-ui-kit/Card';
import { Link } from '@tribeplatform/react-ui-kit/Link';
import { SpaceTagsSelector } from './SpaceTagsSelector.js';
export const HighlightedTags = ({ title, space: _space, spaceId: _spaceId, }) => {
    const { tag: activeTagSlug, tag_id: activeTagId } = useQuery() || {};
    const { mode } = useSlate();
    const { generatePath } = useSpaceUtil();
    const { context: { spaceId: contextSpaceId }, } = useSlate();
    const spaceId = _spaceId || (_space === null || _space === void 0 ? void 0 : _space.id) || contextSpaceId;
    const { data: space } = useSpace({
        variables: { id: spaceId },
        useQueryOptions: {
            enabled: !!spaceId,
        },
    });
    const tagFilterEnabled = !!activeTagSlug || !!activeTagSlug;
    const highlightedTags = space === null || space === void 0 ? void 0 : space.highlightedTags;
    const [canGetPosts] = hasScopesPermission(space, ['getPosts']);
    const [currentTags, setCurrentTags] = useState(mapToSelectableTag(highlightedTags));
    const [isEditing, setIsEditing] = useState(false);
    useEffect(() => {
        setCurrentTags(mapToSelectableTag(highlightedTags));
        setIsEditing(false);
    }, [highlightedTags]);
    const { isAdmin } = useAuthMember();
    const { mutate: updateTags, isLoading: isUpdatingTags } = useUpdateSpaceHighlightedTags({
        useMutationOptions: {
            onSuccess: () => {
                setIsEditing(false);
            },
        },
    });
    const handleUpdateTags = useCallback((tags) => {
        updateTags({
            spaceId,
            input: {
                highlightedTags: tags.map(tag => ({
                    tagId: tag.id,
                })),
            },
        });
    }, [spaceId]);
    if (!canGetPosts || highlightedTags === undefined) {
        return null;
    }
    if (highlightedTags === null || currentTags.length === 0) {
        if (mode === 'edit') {
            return (React.createElement(Card, null,
                React.createElement(Card.Header, { title: title, size: "sm", action: React.createElement(EyeOffIcon, { className: "text-content-subdued w-4 h-4" }) }),
                React.createElement(Card.Content, null,
                    React.createElement("div", { className: "text-sm text-content-subdued" },
                        React.createElement(T, { id: "Blocks.HighlightedTags.NothingToShow", defaultMessage: "There\u2019s nothing to show here, so it\u2019s automatically hidden from members." })))));
        }
        if (!isAdmin) {
            return null;
        }
    }
    return (React.createElement(Card, null,
        React.createElement(Card.Header, { title: title, size: "sm", action: tagFilterEnabled ? (React.createElement(Link, { as: RouterLink, variant: "primary", to: space === null || space === void 0 ? void 0 : space.relativeUrl },
                React.createElement(T, { id: "Generics.Clear", defaultMessage: "Clear" }))) : null }),
        isEditing || currentTags.length > 0 ? (React.createElement(Card.Content, { className: clsx(!isEditing && '-m-1') }, isEditing ? (React.createElement(SpaceTagsSelector, { spaceId: spaceId, tags: currentTags, setTags: setCurrentTags, createable: false })) : (currentTags.map(({ id, slug, title }) => (React.createElement(TagPillWithLink, { className: "inline-flex m-1", key: id, active: id === activeTagId || slug === activeTagSlug, link: id === activeTagId || slug === activeTagSlug
                ? generatePath(space)
                : generatePath(space, null, { tag: slug }), title: title })))))) : (React.createElement("div", { className: "py-3" })),
        isAdmin && (React.createElement(Card.Footer, { className: "flex justify-end space-s-2" }, isEditing ? (React.createElement(React.Fragment, null,
            React.createElement(Button, { variant: "outline", disabled: isUpdatingTags, onClick: () => {
                    setIsEditing(false);
                    setCurrentTags(mapToSelectableTag(highlightedTags));
                } },
                React.createElement(T, { id: "Generics.Cancel", defaultMessage: "Cancel" })),
            React.createElement(Button, { variant: "primary", loading: isUpdatingTags, onClick: () => {
                    handleUpdateTags(currentTags);
                } },
                React.createElement(T, { id: "Generics.Save", defaultMessage: "Save" })))) : (React.createElement(Button, { fullWidth: true, variant: "outline", onClick: () => {
                setIsEditing(true);
            } },
            React.createElement(T, { id: "Blocks.HighlightedTags.EditHighlightedTagsButton", defaultMessage: "Edit highlighted {TAGS}" })))))));
};
const mapToSelectableTag = (highlightedTags) => {
    return highlightedTags || [];
};
//# sourceMappingURL=Block.js.map